/**
 * Transactions, with their receipts (v9.0.0) — on the Accounts screen, for the
 * entity on screen.
 *
 * A row opens the receipt sheet only when there is something to do there: add
 * a receipt (the key is set, the bank has not reversed it, it is under the
 * cap) or look at one it already has. Otherwise it is plain text — no dead
 * controls. A failed load says so; it is never shown as "no transactions".
 */

import type { ApiTransaction, TransactionsResponse } from '../lib/api';
import { canAddReceipt, transactionAmountText, transactionMeta } from '../lib/receipt-copy';

export type TransactionsView =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; data: TransactionsResponse; loadingMore: boolean };

export interface TransactionsCardOptions {
  view: TransactionsView;
  onOpen: (t: ApiTransaction) => void;
  onMore: () => void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

export function createTransactionsCard(opts: TransactionsCardOptions): HTMLElement {
  const card = el('section', 'card vitals transactions');
  card.setAttribute('aria-labelledby', 'transactions-title');
  const title = el('h2', 'label', 'Transactions');
  title.id = 'transactions-title';
  card.append(title);

  const { view } = opts;
  if (view.status === 'loading') {
    card.append(el('p', 'soft', 'Loading…'));
    return card;
  }
  if (view.status === 'error') {
    card.append(el('p', 'soft', view.message));
    return card;
  }

  const { data } = view;
  card.append(
    el(
      'p',
      'soft transactions-sub',
      data.receiptsEnabled
        ? 'Tap one to attach a receipt — a photo or a PDF.'
        : 'Receipts are switched off until the receipts key is set up on the server.',
    ),
  );

  if (data.transactions.length === 0) {
    card.append(
      el(
        'p',
        'soft',
        'No transactions yet. They arrive from bank accounts assigned to this entity.',
      ),
    );
    return card;
  }

  const list = el('ul', 'txn-list');
  for (const t of data.transactions) {
    const li = el('li');
    const openable =
      t.receipts.length > 0 ||
      canAddReceipt(t, {
        receiptsEnabled: data.receiptsEnabled,
        perTransaction: data.limits.perTransaction,
      });
    const row = el(openable ? 'button' : 'div', 'txn-row');
    row.dataset.txnKey = t.txnKey;
    if (row instanceof HTMLButtonElement) {
      row.type = 'button';
      row.addEventListener('click', () => opts.onOpen(t));
    }
    const head = el('span', 'txn-head');
    head.append(
      el('span', 'txn-name', t.name),
      el('span', 'num txn-amount', transactionAmountText(t)),
    );
    const meta = el('span', 'soft txn-meta', transactionMeta(t));
    if (t.receipts.length) meta.dataset.hasReceipts = 'true';
    row.append(head, meta);
    li.append(row);
    list.append(li);
  }
  card.append(list);

  if (data.next) {
    const more = el('button', 'btn-quiet txn-more', view.loadingMore ? 'Loading…' : 'Show older');
    more.type = 'button';
    more.disabled = view.loadingMore;
    more.addEventListener('click', () => opts.onMore());
    card.append(more);
  }
  return card;
}
